import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import useSeo from '@/hooks/useSeo';
import { ASSETS } from '@/constants/assets';

const helpCards = [
  {
    title: 'Serviços Industriais',
    description:
      'Soluções completas para manutenção industrial com atuação em mecânica industrial, equipamentos rotativos e estáticos, sistemas hidráulicos, caldeiraria, soldagem, lubrificação industrial, filtragem e desidratação de óleo, limpeza técnica de unidades hidráulicas, reparos hidráulicos em campo, substituição de vedações, mangueiras, cilindros e componentes hidráulicos, além de apoio técnico especializado para indústrias de papel e celulose, mineração, siderurgia e demais segmentos industriais.',
    icon: 'ri-building-2-line',
    to: '/servicos-de-campo',
  },
  {
    title: 'Componentes Hidráulicos',
    description:
      'Fornecimento de mangueiras hidráulicas, conexões, válvulas, cilindros, bombas, motores hidráulicos, filtros, acumuladores hidráulicos e demais componentes para sistemas hidráulicos industriais.',
    icon: 'ri-tools-line',
    to: '/equipamentos-e-componentes',
  },
  {
    title: 'Reparo em Oficina',
    description:
      'Reparo, recuperação e testes de bombas hidráulicas, cilindros hidráulicos, motores hidráulicos, válvulas, acumuladores hidráulicos, redutores industriais, unidades hidráulicas, trocadores de calor e demais componentes industriais, com estrutura própria, equipe especializada e foco em confiabilidade operacional.',
    icon: 'ri-hammer-line',
    to: '/oficina',
  },
  {
    title: 'Locação de Equipamentos',
    description:
      'Locação de termovácuo, unidades de filtragem de óleo, equipamentos hidráulicos e soluções para apoio à manutenção industrial, grandes paradas e intervenções programadas.',
    icon: 'ri-exchange-line',
    to: '/locacao-equipamentos-industriais',
  },
];

const contactInfo = [
  {
    label: 'Telefone / WhatsApp',
    value: '+55 (27) 99309-2922',
    href: 'https://wa.me/5527993092922',
    icon: 'ri-whatsapp-line',
  },
  {
    label: 'E-mail Principal',
    value: 'oficina@pilar-aracruz.com.br',
    href: 'mailto:oficina@pilar-aracruz.com.br',
    icon: 'ri-mail-line',
  },
  {
    label: 'E-mail Recrutamento',
    value: 'recrutamento@pilar-aracruz.com.br',
    href: 'mailto:recrutamento@pilar-aracruz.com.br',
    icon: 'ri-user-add-line',
  },
  {
    label: 'Localização',
    value: 'Aracruz - Espírito Santo',
    href: null,
    icon: 'ri-map-pin-line',
  },
];

function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!heroRef.current) return;
      const scrollY = window.scrollY;
      const opacity = Math.max(0, 1 - scrollY / 600);
      heroRef.current.style.opacity = String(opacity);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-[90vh] flex items-center overflow-hidden bg-gp-graphite"
    >
      <div className="absolute inset-0">
        <img
          src="/images/CONTATO/HERO/HERO-CONTATO.webp"
          alt="Equipe de suporte técnico industrial do Grupo Pilar pronta para atender sua demanda"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gp-graphite/75"></div>
      </div>
      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 pt-24">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-[2px] bg-gp-yellow"></div>
            <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">
              Atendimento
            </span>
          </div>

          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-bold text-white uppercase leading-[0.95] mb-8">
            Entre em Contato<br />
            <span className="text-gp-yellow">Com a Pilar</span>
          </h1>

          <p className="text-gray-300 text-lg md:text-xl max-w-2xl leading-relaxed mb-10">
            Nossa equipe está pronta para atender sua demanda com agilidade, segurança, conhecimento técnico e experiência industrial.
          </p>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <a
              href="https://wa.me/5527993092922"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-sm font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
            >
              <div className="w-9 h-9 bg-gp-graphite rounded-full flex items-center justify-center group-hover:bg-white transition-colors duration-300">
                <i className="ri-whatsapp-line text-gp-yellow group-hover:text-gp-graphite w-5 h-5 flex items-center justify-center transition-colors duration-300"></i>
              </div>
              <span>Falar no WhatsApp</span>
              <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
            </a>

            <a
              href="#formulario"
              className="inline-flex items-center gap-3 border border-gp-yellow text-gp-yellow hover:bg-gp-yellow hover:text-gp-graphite px-8 py-4 rounded-sm font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
            >
              <i className="ri-send-plane-line w-5 h-5 flex items-center justify-center"></i>
              <span>Solicitar Orçamento</span>
            </a>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <a href="#como-ajudar" className="text-gray-500 hover:text-gp-yellow transition-colors duration-300">
          <i className="ri-arrow-down-line text-2xl w-8 h-8 flex items-center justify-center"></i>
        </a>
      </div>
    </section>
  );
}

function ComoAjudarSection() {
  return (
    <section id="como-ajudar" className="relative bg-gp-graphite py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        ></div>
      </div>

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-[2px] bg-gp-yellow"></div>
            <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">
              Nossas Soluções
            </span>
            <div className="w-12 h-[2px] bg-gp-yellow"></div>
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-white uppercase tracking-wider mb-4">
            Como Podemos <span className="text-gp-yellow">Ajudar</span>
          </h2>
          <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto">
            Clique em uma das áreas abaixo para conhecer mais sobre nossas soluções.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {helpCards.map((card) => (
            <Link
              key={card.title}
              to={card.to}
              className="group bg-gp-graphite-light border border-gp-graphite-lighter hover:border-gp-yellow/40 rounded-lg p-6 md:p-8 transition-all duration-500 hover:-translate-y-2"
            >
              <div className="w-14 h-14 bg-gp-yellow/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-gp-yellow transition-colors duration-300">
                <i className={`${card.icon} text-gp-yellow group-hover:text-gp-graphite w-7 h-7 flex items-center justify-center transition-colors duration-300`}></i>
              </div>
              <h3 className="font-display text-lg md:text-xl font-bold text-white uppercase tracking-wider mb-3 group-hover:text-gp-yellow transition-colors duration-300">
                {card.title}
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                {card.description}
              </p>
              <div className="flex items-center gap-2 text-gp-yellow text-sm uppercase tracking-wider font-semibold group-hover:text-white transition-colors duration-300">
                <span>Saiba mais</span>
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function InformacoesSection() {
  return (
    <section className="relative bg-gp-graphite-light py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 2px 2px, rgba(232,185,42,0.3) 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        ></div>
      </div>

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-[2px] bg-gp-yellow"></div>
            <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">
              Informações
            </span>
            <div className="w-12 h-[2px] bg-gp-yellow"></div>
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-white uppercase tracking-wider mb-4">
            Informações de <span className="text-gp-yellow">Contato</span>
          </h2>
          <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto">
            Entre em contato com nossa equipe pelos canais abaixo.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6 max-w-5xl mx-auto">
          {contactInfo.map((info) => (
            <div
              key={info.label}
              className="group flex items-center gap-5 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/40 rounded-lg p-6 md:p-8 transition-all duration-300 hover:bg-gp-graphite-lighter/20"
            >
              <div className="shrink-0 w-14 h-14 bg-gp-yellow/10 rounded-lg flex items-center justify-center group-hover:bg-gp-yellow/20 transition-colors duration-300">
                <i className={`${info.icon} text-gp-yellow text-xl w-7 h-7 flex items-center justify-center`}></i>
              </div>

              <div className="min-w-0 flex-1">
                <p className="text-gray-500 text-xs uppercase tracking-wider mb-1.5 font-medium">
                  {info.label}
                </p>
                {info.href ? (
                  <a
                    href={info.href}
                    target={info.href.startsWith('http') ? '_blank' : undefined}
                    rel={info.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="text-white font-semibold text-sm md:text-base hover:text-gp-yellow transition-colors duration-300 break-words leading-snug block"
                  >
                    {info.value}
                  </a>
                ) : (
                  <p className="text-white font-semibold text-sm md:text-base break-words leading-snug">
                    {info.value}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FormularioSection() {
  const [mensagemCount, setMensagemCount] = useState(0);

  return (
    <section id="formulario" className="relative bg-gp-graphite py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        ></div>
      </div>

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-[2px] bg-gp-yellow"></div>
              <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">
                Formulário
              </span>
              <div className="w-12 h-[2px] bg-gp-yellow"></div>
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-white uppercase tracking-wider mb-4">
              Fale <span className="text-gp-yellow">Conosco</span>
            </h2>
            <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Preencha o formulário abaixo e nossa equipe retornará o mais breve possível.
            </p>
          </div>

          <form
            id="form-contato"
            action="/enviar_contato.php"
            method="POST"
            className="bg-gp-graphite-light border border-gp-graphite-lighter rounded-xl p-6 md:p-10 space-y-6"
          >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="nome"
                    className="block text-gray-300 text-sm font-medium uppercase tracking-wider mb-2"
                  >
                    Nome <span className="text-gp-yellow">*</span>
                  </label>
                  <input
                    type="text"
                    id="nome"
                    name="nome"
                    required
                    placeholder="Seu nome completo"
                    className="w-full bg-gp-graphite border border-gp-graphite-lighter rounded-md px-4 py-3 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-gp-yellow transition-colors duration-300"
                  />
                </div>
                <div>
                  <label
                    htmlFor="empresa"
                    className="block text-gray-300 text-sm font-medium uppercase tracking-wider mb-2"
                  >
                    Empresa
                  </label>
                  <input
                    type="text"
                    id="empresa"
                    name="empresa"
                    placeholder="Nome da empresa"
                    className="w-full bg-gp-graphite border border-gp-graphite-lighter rounded-md px-4 py-3 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-gp-yellow transition-colors duration-300"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="telefone"
                    className="block text-gray-300 text-sm font-medium uppercase tracking-wider mb-2"
                  >
                    Telefone <span className="text-gp-yellow">*</span>
                  </label>
                  <input
                    type="tel"
                    id="telefone"
                    name="telefone"
                    required
                    placeholder="(27) 99999-9999"
                    className="w-full bg-gp-graphite border border-gp-graphite-lighter rounded-md px-4 py-3 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-gp-yellow transition-colors duration-300"
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-gray-300 text-sm font-medium uppercase tracking-wider mb-2"
                  >
                    E-mail
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="seuemail@exemplo.com"
                    className="w-full bg-gp-graphite border border-gp-graphite-lighter rounded-md px-4 py-3 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-gp-yellow transition-colors duration-300"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="assunto"
                  className="block text-gray-300 text-sm font-medium uppercase tracking-wider mb-2"
                >
                  Assunto <span className="text-gp-yellow">*</span>
                </label>
                <select
                  id="assunto"
                  name="assunto"
                  required
                  className="w-full bg-gp-graphite border border-gp-graphite-lighter rounded-md px-4 py-3 text-white text-sm focus:outline-none focus:border-gp-yellow transition-colors duration-300 appearance-none"
                >
                  <option value="" disabled selected>
                    Selecione o assunto
                  </option>
                  <option value="Serviços Industriais">Serviços Industriais</option>
                  <option value="Componentes Hidráulicos">Componentes Hidráulicos</option>
                  <option value="Reparo em Oficina">Reparo em Oficina</option>
                  <option value="Locação de Equipamentos">Locação de Equipamentos</option>
                  <option value="Outros">Outros</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="mensagem"
                  className="block text-gray-300 text-sm font-medium uppercase tracking-wider mb-2"
                >
                  Mensagem <span className="text-gp-yellow">*</span>
                </label>
                <textarea
                  id="mensagem"
                  name="mensagem"
                  rows={5}
                  maxLength={500}
                  required
                  placeholder="Descreva sua demanda com detalhes..."
                  onChange={(e) => setMensagemCount(e.target.value.length)}
                  className="w-full bg-gp-graphite border border-gp-graphite-lighter rounded-md px-4 py-3 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-gp-yellow transition-colors duration-300 resize-none"
                ></textarea>
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-gray-500">Máximo 500 caracteres</span>
                  <span className={`text-xs ${mensagemCount > 500 ? 'text-red-400' : 'text-gray-500'}`}>
                    {mensagemCount}/500
                  </span>
                </div>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  className="w-full bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-sm font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-[1.02] whitespace-nowrap flex items-center justify-center gap-3"
                >
                  <i className="ri-send-plane-line w-5 h-5 flex items-center justify-center"></i>
                  Enviar Solicitação
                </button>
              </div>
            </form>
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="relative min-h-[500px] md:min-h-[600px] flex items-center overflow-hidden bg-gp-graphite">
      <div className="absolute inset-0">
        <img
          src="/images/CONTATO/CTA/CTA-CONTATO.webp"
          alt="Suporte industrial especializado Grupo Pilar - solicite seu orçamento"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gp-graphite/70"></div>
      </div>
      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 py-20">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-[2px] bg-gp-yellow"></div>
            <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">
              Suporte Especializado
            </span>
            <div className="w-12 h-[2px] bg-gp-yellow"></div>
          </div>

          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold text-white uppercase tracking-wider mb-6">
            Precisa de Suporte<br />
            <span className="text-gp-yellow">Industrial Especializado?</span>
          </h2>

          <p className="text-gray-300 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            Nossa equipe está preparada para atender sua empresa com rapidez, segurança, qualidade e soluções técnicas para manutenção industrial, hidráulica, reparos e locação de equipamentos.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://wa.me/5527993092922"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
            >
              <div className="w-10 h-10 bg-gp-graphite rounded-full flex items-center justify-center group-hover:bg-white transition-colors duration-300">
                <i className="ri-whatsapp-line text-gp-yellow group-hover:text-gp-graphite w-5 h-5 flex items-center justify-center transition-colors duration-300"></i>
              </div>
              <span>Solicitar Orçamento</span>
              <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
            </a>

            <a
              href="https://wa.me/5527993092922"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 border border-gp-yellow text-gp-yellow hover:bg-gp-yellow hover:text-gp-graphite px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
            >
              <i className="ri-customer-service-2-line w-5 h-5 flex items-center justify-center"></i>
              <span>Falar com Especialista</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function BackToHomeSection() {
  return (
    <section className="py-10 border-t border-gp-graphite-lighter bg-gp-graphite">
      <div className="px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider"
          >
            <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
            Voltar para a Home
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function ContatoPage() {
  useSeo({
    title: 'Contato | Pilar Manutenção Industrial',
    description: 'Entre em contato com a Pilar para solicitar suporte técnico, manutenção industrial, reparos, componentes hidráulicos ou locação de equipamentos.',
    keywords: 'contato Pilar, manutenção industrial, hidráulica industrial, reparos, componentes hidráulicos, locação de equipamentos, Aracruz, ES',
    canonical: 'https://www.pilarmanutencao.com.br/contato',
    ogUrl: 'https://www.pilarmanutencao.com.br/contato',
    ogImage: ASSETS.logoPilar,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="min-h-screen bg-gp-graphite">
      <Navbar />
      <main>
        <HeroSection />
        <ComoAjudarSection />
        <InformacoesSection />
        <FormularioSection />
        <CTASection />
        <BackToHomeSection />
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}